"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

import { useAuth } from "@/features/auth";
import { useSubscription } from "@/features/billing/hooks/use-subscription";
import { Badge } from "@/features/settings/components/users/badge";
import {
  SETTINGS_ORG_NAV,
  SETTINGS_PERSONAL_NAV,
  isNavItemVisible,
  isSettingsNavActive,
  type SettingsNavItem,
} from "@/features/settings/components/layout/settings-nav-items";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";
import { useSidebar } from "@/stores/sidebar-store";

import { PanelBackButton } from "./panel-back-button";

function SettingsPanelRow({
  item,
  active,
  hideUpgrade,
  onActiveClick,
}: {
  item: SettingsNavItem;
  active: boolean;
  hideUpgrade: boolean;
  onActiveClick: () => void;
}) {
  const ItemIcon = item.icon;

  if (item.disabled) {
    return (
      <div
        className="flex h-8 w-full cursor-not-allowed select-none items-center gap-2 rounded-md px-2 opacity-50"
        aria-disabled="true"
      >
        <ItemIcon
          width={16}
          height={16}
          className="size-4 shrink-0 text-contrast opacity-[0.45]"
        />
        <span className="truncate text-sm font-medium leading-5 text-contrast/50">
          {item.label}
        </span>
        {item.soon && (
          <Badge
            bg="bg-contrast/6"
            border="border-contrast/8"
            text="text-contrast/50"
          >
            SOON
          </Badge>
        )}
      </div>
    );
  }

  return (
    <Link
      href={item.href}
      aria-current={active ? "page" : undefined}
      onClick={active ? onActiveClick : undefined}
      className={cn(
        "flex h-8 w-full items-center gap-2 rounded-md px-2 transition-colors",
        active
          ? "sidebar-active text-heading"
          : "text-contrast/65 hover:bg-contrast/4 hover:text-heading",
      )}
    >
      <ItemIcon
        width={16}
        height={16}
        className={cn(
          "size-4 shrink-0 text-contrast",
          active ? "opacity-90" : "opacity-[0.45]",
        )}
      />
      <span className="flex-1 truncate text-sm font-medium leading-5">
        {item.label}
      </span>
      {item.upgradeIcon && !hideUpgrade && (
        <Icon
          name="IconArrowUpCircle"
          size={14}
          className="shrink-0 text-[#FFAF53]"
        />
      )}
    </Link>
  );
}

export function SettingsPanel({ onBack }: { onBack: () => void }) {
  const pathname = usePathname();
  const { isAdmin } = useAuth();
  const { data: subscription } = useSubscription();
  const { setOpenMobile } = useSidebar();
  const hasAtsAddon =
    subscription?.addons?.some(
      (a) => a.slug === "custom_ats" && a.status === "active",
    ) ?? false;

  const renderRow = (item: SettingsNavItem) => (
    <SettingsPanelRow
      key={item.href}
      item={item}
      active={isSettingsNavActive(pathname, item.href)}
      hideUpgrade={hasAtsAddon}
      onActiveClick={() => setOpenMobile(false)}
    />
  );

  return (
    <nav
      aria-label="Settings"
      className="flex flex-col gap-0.5 px-3.5 py-2.5"
    >
      <PanelBackButton onBack={onBack} />

      <p className="px-2 py-1 text-xs font-medium leading-4 text-contrast/40">
        Personal
      </p>
      {SETTINGS_PERSONAL_NAV.filter((item) =>
        isNavItemVisible(item, subscription),
      ).map(renderRow)}
      {isAdmin && (
        <>
          <p className="mt-1 px-2 py-1 text-xs font-medium leading-4 text-contrast/40">
            Organization
          </p>
          {SETTINGS_ORG_NAV.filter((item) =>
            isNavItemVisible(item, subscription),
          ).map(renderRow)}
        </>
      )}
    </nav>
  );
}
